import { useStore } from "react-redux";
import { useRef } from "react";
import type { TradeInAemActionsType, TradeInAemStateType, TradeInAemStoreType } from "../redux/store";
import { TradeInCslTz } from "./implementations/standalone/tz";
import type { TradeInCsl } from "./TradeInCsl";
import { TradeInCslFi } from "./implementations/standalone/fi";
import { TradeInCslSw } from "./implementations/standalone/sw";
import { TradeInAemVersion } from "../utils/constants";
import { TradeInCslTzIntegrated } from "./implementations/integrated/tz-integrated";
import { TradeInCslGb } from "./implementations/standalone/gb";
import { TradeInCslGbIntegrated } from "./implementations/integrated/gb-integrated";
import { TradeInCslIt } from "./implementations/standalone/it";
import { TradeInCslFiHidden } from "./implementations/hidden/fi-hidden";
import { TradeInCslGbHidden } from "./implementations/hidden/gb-hidden";
import { TradeInCslGr } from "./implementations/standalone/gr";
import { TradeInCslEs } from "./implementations/standalone/es";
import { TradeInCslBg } from "./implementations/standalone/bg";

// TS-magic found in an obscure SO answer. This type indicates an implementation of an abstract class.
export type TradeInCslImplementation = { new (): TradeInCsl } & typeof TradeInCsl;

type TradeInCslMap = Record<
    "toyota" | "lexus",
    Record<
        string,
        {
            [TradeInAemVersion.Integrated]?: TradeInCslImplementation;
            [TradeInAemVersion.Standalone]?: TradeInCslImplementation;
            [TradeInAemVersion.Hidden]?: TradeInCslImplementation;
        }
    >
>;

// Mapping for Trade-in implementations.
// Use brand/country-language mapping to future proof the implementation.
// This is explicitly not exported, use the helper functions below to access an implementation.
const tradeInCslMap: TradeInCslMap = {
    toyota: {
        "tz-en": {
            [TradeInAemVersion.Standalone]: TradeInCslTz,
            [TradeInAemVersion.Integrated]: TradeInCslTzIntegrated,
        }, // Test case, use this for development or easy feature testing
        "fi-fi": { [TradeInAemVersion.Standalone]: TradeInCslFi, [TradeInAemVersion.Hidden]: TradeInCslFiHidden },
        "se-sv": { [TradeInAemVersion.Standalone]: TradeInCslSw },
        "gb-en": {
            [TradeInAemVersion.Standalone]: TradeInCslGb,
            [TradeInAemVersion.Integrated]: TradeInCslGbIntegrated,
            [TradeInAemVersion.Hidden]: TradeInCslGbHidden,
        },
        "it-it": { [TradeInAemVersion.Standalone]: TradeInCslIt },
        "gr-el": { [TradeInAemVersion.Standalone]: TradeInCslGr },
        "es-es": { [TradeInAemVersion.Standalone]: TradeInCslEs },
        "bg-bg": { [TradeInAemVersion.Standalone]: TradeInCslBg },
    },
    lexus: {
        "tz-en": { [TradeInAemVersion.Standalone]: TradeInCslTz }, // Test case, use this for development or easy feature testing
        "fi-fi": { [TradeInAemVersion.Standalone]: TradeInCslFi, [TradeInAemVersion.Hidden]: TradeInCslFiHidden },
        "se-sv": { [TradeInAemVersion.Standalone]: TradeInCslSw },
        "gb-en": {
            [TradeInAemVersion.Standalone]: TradeInCslGb,
            [TradeInAemVersion.Integrated]: TradeInCslGbIntegrated,
            [TradeInAemVersion.Hidden]: TradeInCslGbHidden,
        },
        "gr-el": { [TradeInAemVersion.Standalone]: TradeInCslGr },
        "es-es": { [TradeInAemVersion.Standalone]: TradeInCslEs },
        "bg-bg": { [TradeInAemVersion.Standalone]: TradeInCslBg },
    },
};

/**
 * Used to retrieve and initiate a Trade in CSL
 * Never use this directly, unless it's a specific edge case which doesn't require the redux store
 * In all other cases use 'useTradeInCsl'
 */
export const getTradeInCsl = (
    brand: "toyota" | "lexus",
    country: string,
    language: string,
    version: TradeInAemVersion,
): TradeInCsl => {
    // Retrieve the CSL for the current country/language/brand setup.
    const TradeInCslImplementation = tradeInCslMap?.[brand]?.[`${country}-${language}`]?.[version];

    if (!TradeInCslImplementation) {
        throw new Error(`Trade-in ${brand}-${country}-${language}-${version} not found`, {
            cause: "This trade-in version is not supported in this country",
        });
    }

    return new TradeInCslImplementation();
};

/**
 * Retrieve the correct CSL implementation based on the country.
 * This will throw if it doesnt find an implementation but that is on purpose. Trade-in will break anyway.
 */
export const getInitializedTradeInCsl = (store: TradeInAemStoreType): TradeInCsl => {
    const { commonSettings, tradeInAemUtils } = store.getState();
    const { country, language, brand } = commonSettings;
    const { version } = tradeInAemUtils;

    // Retrieve the CSL for the current country/language/brand setup.
    const csl = getTradeInCsl(brand, country, language, version);
    csl.setStore(store);

    return csl;
};

/**
 * A hook for easy access to CSL inside a React component.
 * As CSL will be used in lots of places, not having to always read commonSettings manually should come in handy and improve readability.
 */
export const useTradeInCsl = (): TradeInCsl => {
    const store = useStore<TradeInAemStateType, TradeInAemActionsType>();

    // Use a ref to avoid potential rerender/instancing issues.
    const cslRef = useRef(getInitializedTradeInCsl(store));

    return cslRef.current;
};
